import { FC, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { AppSettings, useAppDataContext } from "@context/AppContext";
import { gameState } from "@/services/gameState";
import { InfoRounded } from "@mui/icons-material";

const SettingsDialog: FC<{
  open: boolean;
  onClose: (newSettings: Partial<AppSettings>) => void;
}> = ({ open, onClose }) => {
  const {
    highlightSetterAutomatically,
    useFirstRefPerspective,
    useHighlightFor,
    showPlayerNames,
    usePlayerFirstNames,
    challengeOverrideRemaining,
    challengeTickerLocation,
    currentView,
    attemptAlwaysOnScreen,
  } = useAppDataContext();

  const [
    prelimHighlightSetterAutomatically,
    setPrelimHighlightSetterAutomatically,
  ] = useState(highlightSetterAutomatically);
  const [prelimUseFirstRefPerspective, setPrelimUseFirstRefPerspective] =
    useState(useFirstRefPerspective);
  const [prelimUseHighlightFor, setPrelimUseHighlightFor] =
    useState(useHighlightFor);
  const [prelimShowPlayerNames, setPrelimShowPlayerNames] =
    useState(showPlayerNames);
  const [prelimUsePlayerFirstNames, setPrelimUsePlayerFirstNames] =
    useState(usePlayerFirstNames);
  const [prelimChallengeTickerLocation, setPrelimChallengeTickerLocation] =
    useState(challengeTickerLocation);
  const [
    prelimChallengeOverrideRemaining,
    setPrelimChallengeOverrideRemaining,
  ] = useState(challengeOverrideRemaining);
  const [prelimAttemptAlwaysOnScreen, setPrelimAttemptAlwaysOnScreen] =
    useState(attemptAlwaysOnScreen);

  useEffect(() => {
    setPrelimHighlightSetterAutomatically(highlightSetterAutomatically);
    setPrelimUseFirstRefPerspective(useFirstRefPerspective);
    setPrelimUseHighlightFor(useHighlightFor);
    setPrelimShowPlayerNames(showPlayerNames);
    setPrelimUsePlayerFirstNames(usePlayerFirstNames);
    setPrelimChallengeTickerLocation(challengeTickerLocation);
    setPrelimChallengeOverrideRemaining(challengeOverrideRemaining);
    setPrelimAttemptAlwaysOnScreen(attemptAlwaysOnScreen);
  }, [
    highlightSetterAutomatically,
    useFirstRefPerspective,
    useHighlightFor,
    showPlayerNames,
    usePlayerFirstNames,
    challengeTickerLocation,
    challengeOverrideRemaining,
    attemptAlwaysOnScreen,
  ]);

  const scoreConfiguration = gameState("scoreConfiguration");
  const isSetterPlayerKnown = gameState("isSetterPlayerKnown");

  const disableChallengeOverrideRemaining =
    !scoreConfiguration.videoChallengeAvailable;

  return (
    <Dialog
      open={open}
      onClose={() => onClose({})}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Einstellungen</DialogTitle>
      <DialogContent>
        <FormControlLabel
          control={
            <Switch
              checked={prelimUseFirstRefPerspective}
              onChange={(event) =>
                setPrelimUseFirstRefPerspective(event.target.checked)
              }
              inputProps={{ "aria-label": "controlled" }}
            />
          }
          label={`Perspektive aus Sicht des ${prelimUseFirstRefPerspective ? "1. Schiris" : "Schreibertisches"}`}
        />
        {currentView === "challenge" && (
          <>
            <Divider variant={"middle"} sx={{ my: 1 }} />
            <FormControlLabel
              control={
                <Switch
                  disabled={disableChallengeOverrideRemaining}
                  checked={
                    disableChallengeOverrideRemaining ||
                    prelimChallengeOverrideRemaining
                  }
                  onChange={(event) =>
                    setPrelimChallengeOverrideRemaining(event.target.checked)
                  }
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {`Verbleibende Challenges aus SAMS überschreiben`}
                  {disableChallengeOverrideRemaining && (
                    <Tooltip
                      onClick={(e) => {
                        e.preventDefault();
                      }}
                      enterTouchDelay={0}
                      title={`Bei diesem Spiel sind die Challenges in SAMS deaktiviert. 
                        Daher wird die Anzahl der verbleibenden Challenges überschrieben. 
                        Diese Einstellung gilt nur für dieses Spiel.
                        In einem Spiel mit Challenges in SAMS kann hier ausgewählt werden, ob die verbleibenden Challenges aus SAMS übernommen werden sollen oder nicht.`}
                    >
                      <InfoRounded />
                    </Tooltip>
                  )}
                </Box>
              }
            />
            <FormControl fullWidth sx={{ my: 2, gap: 0 }}>
              <FormLabel>Anordnung Spielverlauf</FormLabel>
              <ToggleButtonGroup
                exclusive
                value={prelimChallengeTickerLocation}
                onChange={(event, value) =>
                  setPrelimChallengeTickerLocation(
                    value as AppSettings["challengeTickerLocation"],
                  )
                }
              >
                <ToggleButton value={"left"}>Links</ToggleButton>
                <ToggleButton value={"right"}>Rechts</ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
          </>
        )}
        <Divider variant={"middle"} sx={{ my: 1 }} />
        <FormControlLabel
          control={
            <>
              <Switch
                disabled={!isSetterPlayerKnown}
                checked={prelimHighlightSetterAutomatically}
                onChange={(event) => {
                  setPrelimHighlightSetterAutomatically(event.target.checked);
                  if (event.target.checked) {
                    setPrelimUseHighlightFor("gameCaptain");
                  }
                }}
                inputProps={{ "aria-label": "controlled" }}
              />
            </>
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {`Steller:in automatisch markieren`}
              {!isSetterPlayerKnown && (
                <Tooltip
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                  enterTouchDelay={0}
                  title={
                    "Nicht verfügbar, wenn die Zuspieler nicht in SAMS hinterlegt sind"
                  }
                >
                  <InfoRounded />
                </Tooltip>
              )}
            </Box>
          }
        />
        <FormControl fullWidth sx={{ my: 2, gap: 1 }}>
          <FormLabel>Markierung nutzen für</FormLabel>
          <ToggleButtonGroup
            exclusive
            value={prelimUseHighlightFor}
            onChange={(event, value) =>
              setPrelimUseHighlightFor(value as AppSettings["useHighlightFor"])
            }
          >
            <ToggleButton value="gameCaptain">Spielkapitän:in</ToggleButton>
            <ToggleButton
              disabled={prelimHighlightSetterAutomatically}
              value="setter"
            >
              Zuspieler:in
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>

        {currentView === "tablet" && (
          <>
            <Divider variant={"middle"} sx={{ my: 1 }} />

            <FormControlLabel
              sx={{ my: 2 }}
              control={
                <Switch
                  checked={prelimAttemptAlwaysOnScreen}
                  onChange={(event) => {
                    setPrelimAttemptAlwaysOnScreen(event.target.checked);
                  }}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {`Bildschirmsperre blockieren ("Stay awake")`}
                  <Tooltip
                    onClick={(e) => {
                      e.preventDefault();
                    }}
                    enterTouchDelay={0}
                    title={`Aktivieren, um die Bildschirmsperre des Geräts zu blockieren.
                    Je nach Einstellungen des Geräts und Browsers kann dies Funktion blockiert sein.
                    Ob das Gerät die Bildschirmsperre blockiert, wird unten in der Statusleiste angezeigt ("Stay awake: ON").
                    Wenn das Gerät die Bildschirmsperre nicht blockieren kann, wird "N/A" angezeigt.
                    Wenn das Gerät die Bildschirmsperre durchsetzt, wird "OFF" angezeigt.`}
                  >
                    <InfoRounded />
                  </Tooltip>
                </Box>
              }
            />

            <Divider variant={"middle"} sx={{ my: 1 }} />

            <FormControlLabel
              sx={{ my: 2 }}
              control={
                <Switch
                  checked={prelimShowPlayerNames}
                  onChange={(event) => {
                    setPrelimShowPlayerNames(event.target.checked);
                  }}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {`Spielernamen anzeigen`}
                </Box>
              }
            />

            <FormControl fullWidth sx={{ my: 2, gap: 0 }}>
              <ToggleButtonGroup
                exclusive
                disabled={!prelimShowPlayerNames}
                value={prelimUsePlayerFirstNames}
                onChange={(event, value) =>
                  setPrelimUsePlayerFirstNames(
                    value as AppSettings["usePlayerFirstNames"],
                  )
                }
              >
                <ToggleButton value={true}>Vorname</ToggleButton>
                <ToggleButton value={false}>Nachname</ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() =>
            onClose({
              useFirstRefPerspective: prelimUseFirstRefPerspective,
              highlightSetterAutomatically: prelimHighlightSetterAutomatically,
              useHighlightFor: prelimUseHighlightFor,
              usePlayerFirstNames: prelimUsePlayerFirstNames,
              showPlayerNames: prelimShowPlayerNames,
              challengeTickerLocation: prelimChallengeTickerLocation,
              challengeOverrideRemaining: prelimChallengeOverrideRemaining,
              attemptAlwaysOnScreen: prelimAttemptAlwaysOnScreen,
            })
          }
          autoFocus
        >
          Speichern
        </Button>
        <Button onClick={() => onClose({})}>Abbrechen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDialog;
